import BrandLogo from "../components/BrandLogo";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Store, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

export default function RegisterPage() {
  const [form, setForm] = useState({
    store_name: "", business_type: "retail", contact_name: "", email: "", phone: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [bizTypes, setBizTypes] = useState(["pharmacy", "grocery", "retail", "cafe", "clinic"]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/config/business-types").then(({ data }) => setBizTypes(data.types)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/apply", { ...form, email: form.email || null, phone: form.phone || null });
      setDone(true);
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-6">
      <div className="w-full max-w-md space-y-8 bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
        <div className="space-y-2">
          <div className="flex items-center gap-2 mb-4">
            <BrandLogo />
            <span className="font-heading font-bold text-lg">MatteusMx</span>
          </div>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Apply for an account</h2>
          <p className="text-sm text-slate-500">
            Submit your business details. Our platform administrator reviews every application and issues your login credentials.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Store name</Label>
            <Input data-testid="register-store-name-input" value={form.store_name} onChange={set("store_name")} placeholder="e.g. Botika ni Maria" required className="h-11" />
          </div>
          <div className="space-y-1.5">
            <Label>Business type</Label>
            <Select value={form.business_type} onValueChange={(v) => setForm({ ...form, business_type: v })}>
              <SelectTrigger data-testid="register-business-type-select" className="h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                {bizTypes.map((t) => (
                  <SelectItem key={t} value={t} className="capitalize">{t.replace(/_/g, " ")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Contact person</Label>
            <Input data-testid="register-contact-name-input" value={form.contact_name} onChange={set("contact_name")} placeholder="Full name" required className="h-11" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Email <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Input data-testid="register-email-input" type="email" value={form.email} onChange={set("email")} placeholder="you@store.com" className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label>Phone <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Input data-testid="register-phone-input" value={form.phone} onChange={set("phone")} placeholder="09xx xxx xxxx" className="h-11" />
            </div>
          </div>
          <Button type="submit" className="w-full h-11 bg-slate-900 hover:bg-slate-800" disabled={loading} data-testid="register-submit-button">
            {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Submit application
          </Button>
        </form>

        <p className="text-sm text-slate-500">
          Already have credentials?{" "}
          <Link to="/login" className="font-semibold text-slate-900 hover:underline" data-testid="back-to-login-link">Sign in</Link>
        </p>
      </div>

      <Dialog open={done} onOpenChange={() => {}}>
        <DialogContent className="max-w-sm text-center" data-testid="registration-success-popup">
          <DialogHeader>
            <DialogTitle className="font-heading text-center">Application submitted</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="mx-auto h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="h-7 w-7 text-emerald-600" />
            </div>
            <p className="text-sm text-slate-700 leading-relaxed">
              Thank you for your registration. A representative shall contact you for your login credentials.
            </p>
            <Button className="w-full bg-slate-900" onClick={() => navigate("/login")} data-testid="registration-success-ok-btn">
              Back to sign in
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
